import { useBlocker } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';

/**
 * useUnsavedChangesGuard warns before a dirty form's work is thrown away —
 * both on an in-app navigation and on a browser refresh or close.
 *
 * `useBlocker` covers in-app navigation; `enableBeforeUnload` adds the native
 * dialog on top. Without `withResolver` a blocked in-app navigation would be
 * SILENT — the click just does nothing, with nothing to say why — which is
 * worse than losing the work. So the resolver comes back to the caller, which
 * owes the user a dialog offering `proceed` and `reset`.
 *
 * This is deliberately not meant to be reached for at a surface: calling it by
 * hand is the thing that rots, because a form added later simply never calls
 * it and nothing fails. `<Form>` in components/form.tsx is the only caller,
 * and an eslint rule keeps raw `<form>` elements out of src/pages so that
 * wiring a new form means using the component rather than remembering a hook.
 */
export function useUnsavedChangesGuard<
  TFieldValues extends FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(form: UseFormReturn<TFieldValues, TContext, TTransformedValues>) {
  // `form.formState` is a proxy over the RENDERED state snapshot, not over
  // react-hook-form's live internals. It also only maintains the keys a render
  // actually touched — so reading `formState.isDirty` from inside
  // shouldBlockFn gives either a stale value or a permanently false one.
  //
  // That distinction decides whether this guard is usable at all. A form that
  // saves and then redirects calls reset() and navigate() in the same tick:
  // the form is pristine by the time navigation begins, but no render has
  // happened yet, so a snapshot read still says dirty and the guard swallows
  // the form's own redirect — stranding the user on the form they just saved.
  //
  // subscribe() is the live view. Its callback runs when the state changes
  // rather than when React re-renders, so reset() has already landed here by
  // the time navigate() asks.
  const isDirty = useRef(false);

  useEffect(
    () =>
      form.subscribe({
        formState: { isDirty: true },
        callback: ({ isDirty: dirty }) => {
          isDirty.current = dirty ?? false;
        },
      }),
    [form]
  );

  return useBlocker({
    shouldBlockFn: () => isDirty.current,
    enableBeforeUnload: () => isDirty.current,
    withResolver: true,
  });
}
